import { AlertTriangle, PauseCircle } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { statusIntent } from "@/design";
import { Tooltip } from "@/shared/ui/tooltip";

import type { AttentionReason } from "../api/use-orders";
import { topReviewReason } from "../lib/review-flags";
import type { Order } from "../lib/types";

const ICON: Record<AttentionReason, typeof AlertTriangle> = {
  oversold: AlertTriangle,
  buyer_cancel_requested: PauseCircle,
};

// review_meta field carrying the tooltip's "key fact" for a reason, when the
// BE included it (review_meta is an untyped per-reason bag — schema.d.ts has
// no structured shape for it, so these reads are defensive).
const KEY_FACT_FIELD: Partial<Record<AttentionReason, string>> = {
  oversold: "sku",
  buyer_cancel_requested: "respond_by",
};

const INTENT_FG: Record<string, string> = {
  danger: "text-[var(--intent-danger-fg)]",
  warning: "text-[var(--intent-warning-fg)]",
};

interface ReviewFlagIconProps {
  reasons: Order["reviewReasons"];
  meta?: Order["reviewMeta"];
}

/** Highest-severity exception flag for a row, with a tooltip of its label + key fact. Renders nothing for a healthy order or one flagged only with an ingest-stage reason (sku_unmapped etc. — those already surface via exception_only, no icon here). */
export function ReviewFlagIcon({ reasons, meta }: ReviewFlagIconProps): JSX.Element | null {
  const { t } = useTranslation("orders");

  const top = topReviewReason(reasons);
  if (!top) return null;

  const Icon = ICON[top];
  const field = KEY_FACT_FIELD[top];
  const fact = field ? meta?.[top]?.[field] : undefined;
  const label = t(`reason.${top}`);
  const tooltip =
    fact !== undefined && fact !== null && fact !== "" ? `${label} · ${String(fact)}` : label;
  const intent = statusIntent("review_reason", top);

  return (
    <Tooltip content={tooltip}>
      <Icon
        className={`h-3.5 w-3.5 shrink-0 ${INTENT_FG[intent] ?? ""}`}
        aria-label={tooltip}
        data-testid={`order-${top}-icon`}
      />
    </Tooltip>
  );
}
